import React from "react";

interface MessageContentProps {
    content: string;
    isUserMessage?: boolean;
}

/**
 * Renders chat message content with support for:
 * - Markdown headers (# ## ###)
 * - Code blocks (```language\ncode\n```)
 * - Inline code (`code`)
 * - Bold text (**text**)
 */
export const CindraChatMessageContent: React.FC<MessageContentProps> = ({ content, isUserMessage = false }) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmedLine = line.trim();

        // Check for code block start (```language or ```)
        if (trimmedLine.startsWith('```')) {
            const language = trimmedLine.substring(3).trim() || 'plaintext';
            const codeLines: string[] = [];
            i++; // Move past the opening ```

            // Collect all lines until closing ```
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }

            // Render code block
            elements.push(
                <div key={`code-${i}`} className="my-2 rounded-md overflow-hidden">
                    <div className="bg-gray-800 text-gray-200 px-3 py-1 text-xs font-mono border-b border-gray-700">
                        {language}
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-3 overflow-x-auto text-sm font-mono">
                        <code>{codeLines.join('\n')}</code>
                    </pre>
                </div>
            );

            i++; // Move past the closing ```
            continue;
        }

        // Render regular line with inline formatting
        elements.push(
            <p key={`line-${i}`} className="mb-2 last:mb-0 select-text">
                {renderInlineFormatting(line)}
            </p>
        );

        i++;
    }

    return <div className="prose prose-sm max-w-none select-text">{elements}</div>;
};

/**
 * Renders a single line with inline formatting:
 * - Headers (# ## ###)
 * - Inline code (`code`)
 * - Bold text (**text**)
 */
function renderInlineFormatting(line: string): React.ReactNode {
    const trimmedLine = line.trim();

    // Check for headers
    if (trimmedLine.startsWith("# ")) {
        return <strong className="text-lg select-text">{trimmedLine.substring(2)}</strong>;
    }
    if (trimmedLine.startsWith("## ")) {
        return <strong className="text-base select-text">{trimmedLine.substring(3)}</strong>;
    }
    if (trimmedLine.startsWith("### ")) {
        return <strong className="text-sm select-text">{trimmedLine.substring(4)}</strong>;
    }

    // Parse inline code and bold
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;

    // Pattern to match: `code` or **bold**
    const inlinePattern = /(`[^`]+`|\*\*[^*]+\*\*)/g;
    let match;
    let lastIndex = 0;

    while ((match = inlinePattern.exec(remaining)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push(
                <span key={`text-${key++}`}>
                    {remaining.substring(lastIndex, match.index)}
                </span>
            );
        }

        const matchedText = match[0];

        // Inline code
        if (matchedText.startsWith('`') && matchedText.endsWith('`')) {
            parts.push(
                <code
                    key={`code-${key++}`}
                    className="bg-gray-800 text-gray-100 px-1.5 py-0.5 rounded text-xs font-mono"
                >
                    {matchedText.slice(1, -1)}
                </code>
            );
        }
        // Bold
        else if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
            parts.push(
                <strong key={`bold-${key++}`} className="select-text">
                    {matchedText.slice(2, -2)}
                </strong>
            );
        }

        lastIndex = match.index + matchedText.length;
    }

    // Add remaining text
    if (lastIndex < remaining.length) {
        parts.push(
            <span key={`text-${key++}`}>
                {remaining.substring(lastIndex)}
            </span>
        );
    }

    return parts.length > 0 ? <>{parts}</> : <span className="select-text">{line}</span>;
}
